import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import RenovationTypes from './components/RenovationTypes';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://hornpmc.ca';

const pageSeo = {
  title: 'Home Renovations Burlington ON – Kitchen, Bathroom & More | Horn PMC',
  description:
    'Full home renovations in Burlington, Oakville & Hamilton. Kitchen remodels, bathroom upgrades, basement finishing, additions, flooring & exterior work. Free estimates — call Horn PMC at 905-869-4676.',
  canonical: `${siteUrl}/home-renovations`,
  keywords: 'home renovations Burlington, kitchen renovation Burlington, bathroom remodel Burlington, basement finishing Oakville Hamilton',
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Home Renovation Services',
  description:
    'Full home renovation services including kitchen remodels, bathroom renovations, basement finishing, home additions, flooring installation, and exterior renovations for Burlington, Oakville, and Hamilton homeowners.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'Horn Property Maintenance and Construction',
    telephone: '+19058694676',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Burlington',
      addressRegion: 'ON',
      addressCountry: 'CA',
    },
  },
  areaServed: ['Burlington, ON', 'Oakville, ON', 'Hamilton, ON', 'Mississauga, ON', 'Milton, ON'],
  serviceType: [
    'Kitchen Renovation',
    'Bathroom Renovation',
    'Basement Finishing',
    'Home Addition',
    'Flooring Installation',
    'Exterior Renovation',
  ],
  url: pageSeo.canonical,
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
    priceSpecification: {
      '@type': 'PriceSpecification',
      priceCurrency: 'CAD',
      description: 'Free on-site estimates available',
    },
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: pageSeo.title,
  description: pageSeo.description,
  url: pageSeo.canonical,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Horn PMC',
    url: siteUrl,
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Home Renovations', item: pageSeo.canonical },
    ],
  },
};

const galleryItems = [
  {
    id: 1,
    type: 'Kitchen',
    title: 'Full Kitchen Gut & Rebuild',
    location: 'Burlington, ON',
    imageUrl:
      'https://public.readdy.ai/ai/img_res/a856481c24fabe014fd233a914e72784.jpg',
  },
  {
    id: 2,
    type: 'Bathroom',
    title: 'Master Ensuite Renovation',
    location: 'Oakville, ON',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Renovated%20master%20ensuite%20bathroom%20with%20large%20walk-in%20glass%20shower%2C%20freestanding%20soaker%20tub%2C%20double%20floating%20vanity%2C%20matte%20black%20fixtures%2C%20large%20format%20white%20marble%20tile%2C%20bright%20natural%20light%2C%20luxury%20residential%20bathroom%20result%2C%20Oakville%20Ontario%20home&width=600&height=450&seq=rg2&orientation=landscape',
  },
  {
    id: 3,
    type: 'Basement',
    title: 'Basement Entertainment Suite',
    location: 'Burlington, ON',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Finished%20basement%20with%20large%20entertainment%20wall%20built-in%20shelving%2C%20wet%20bar%20area%20with%20wine%20fridge%2C%20luxury%20vinyl%20plank%20flooring%2C%20pot%20lights%2C%20comfortable%20family%20room%20seating%20area%2C%20modern%20residential%20basement%2C%20Burlington%20Ontario%20Canada%20renovation%20result&width=600&height=450&seq=rg3&orientation=landscape',
  },
  {
    id: 4,
    type: 'Flooring',
    title: 'Whole-Home Hardwood Install',
    location: 'Hamilton, ON',
    imageUrl:
      'https://readdy.ai/api/search-image?query=New%20wide%20plank%20white%20oak%20hardwood%20flooring%20installed%20throughout%20open%20concept%20living%20room%20dining%20area%2C%20beautiful%20natural%20wood%20grain%2C%20modern%20baseboards%20and%20transitions%2C%20bright%20airy%20residential%20space%2C%20Hamilton%20Ontario%20home%20renovation%20photography&width=600&height=450&seq=rg4&orientation=landscape',
  },
];

const whyUs = [
  {
    icon: 'ri-file-list-3-line',
    title: 'Detailed Written Quotes',
    desc: 'Every quote is itemized — you see exactly what you\'re paying for before a single nail goes in.',
  },
  {
    icon: 'ri-team-line',
    title: 'One Crew, Start to Finish',
    desc: 'No subcontractor runaround. Our team handles your project from demo to final walkthrough.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'On-Time Delivery',
    desc: 'We build realistic timelines and stick to them. No sitting around waiting for crews to show up.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Full Permit Coordination',
    desc: 'We handle all required permits with the city so you\'re protected and the work is done right.',
  },
];

export default function HomeRenovations() {
  return (
    <>
      <title>{pageSeo.title}</title>
      <meta name="description" content={pageSeo.description} />
      <meta name="keywords" content={pageSeo.keywords} />
      <link rel="canonical" href={pageSeo.canonical} />
      <meta property="og:title" content={pageSeo.title} />
      <meta property="og:description" content={pageSeo.description} />
      <meta property="og:url" content={pageSeo.canonical} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageSeo.title} />
      <meta name="twitter:description" content={pageSeo.description} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <main style={{ fontFamily: 'Inter, sans-serif' }}>
        <Navbar />

        {/* ── Hero ── */}
        <section className="relative w-full h-[520px] flex items-end pb-16">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Stunning%20renovated%20open%20concept%20home%20interior%20with%20new%20kitchen%2C%20hardwood%20floors%2C%20modern%20staircase%2C%20large%20windows%20flooding%20natural%20light%20into%20living%20and%20dining%20space%2C%20beautiful%20residential%20renovation%20result%2C%20Burlington%20Ontario%20Canada%2C%20warm%20tones%2C%20professional%20real%20estate%20photography%2C%20luxury%20home%20transformation&width=1920&height=600&seq=reno-hero&orientation=landscape"
              alt="Home Renovation Services Burlington Ontario"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20"></div>
          </div>

          {/* Breadcrumb */}
          <div className="absolute top-28 left-6 lg:left-16 flex items-center gap-2 text-white/70 text-xs">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Home Renovations</span>
          </div>

          <div className="relative z-10 w-full px-6 lg:px-16">
            <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-3">OUR SERVICES</p>
            <h1
              className="text-white text-5xl lg:text-6xl font-bold leading-tight mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Home<br />Renovations
            </h1>
            <p className="text-white/75 text-base max-w-xl">
              Kitchens, bathrooms, basements and beyond — complete renovation services for Burlington,
              Oakville, and Hamilton homeowners.
            </p>
          </div>
        </section>

        {/* ── Intro + Why Us ── */}
        <section className="bg-white py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-14 items-center mb-16">
              <div className="flex-1">
                <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">WHY HORN PMC</p>
                <h2
                  className="text-[#2B5741] text-3xl font-bold mb-5"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Burlington's Home Renovation Specialists
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  At Horn PMC, we believe renovations should be exciting — not stressful. That's why we keep
                  every project organized, transparent, and on schedule from the very first meeting to the final
                  walkthrough.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Whether you're updating a single bathroom or transforming your entire main floor, our
                  experienced crew brings the same care and craftsmanship to every job. Honest pricing, clean
                  job sites, and results that last.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/#contact"
                    className="bg-[#2B5741] text-white px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#3A7060] transition-colors whitespace-nowrap rounded-md text-center"
                  >
                    GET A FREE ESTIMATE
                  </a>
                  <a
                    href="tel:9058694676"
                    className="flex items-center justify-center gap-2 border-2 border-[#2B5741] text-[#2B5741] px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#2B5741] hover:text-white transition-colors whitespace-nowrap rounded-md"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-fill"></i>
                    </div>
                    905-869-4676
                  </a>
                </div>
              </div>
              <div className="lg:w-96 w-full h-72 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Skilled%20contractor%20reviewing%20renovation%20blueprints%20with%20homeowner%20at%20kitchen%20counter%2C%20professional%20renovation%20consultation%2C%20residential%20home%20interior%20setting%2C%20warm%20natural%20light%2C%20two%20people%20collaborating%20on%20home%20improvement%20project%2C%20Burlington%20Ontario%20Canada&width=700&height=520&seq=reno-intro&orientation=landscape"
                  alt="Horn PMC renovation consultation Burlington Ontario"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Why Us grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {whyUs.map((item) => (
                <div key={item.title} className="flex gap-4 p-5 rounded-lg bg-[#F7F5F1]">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#2B5741] rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <p className="text-gray-800 font-semibold text-sm mb-1">{item.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Interactive Renovation Types ── */}
        <RenovationTypes />

        {/* ── Our Process ── */}
        <section className="bg-white py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">HOW IT WORKS</p>
              <h2
                className="text-[#2B5741] text-4xl font-bold"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Our Renovation Process
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: '01', title: 'Free Consultation', desc: 'We visit your property, discuss your vision and walk you through options with zero pressure.' },
                { step: '02', title: 'Detailed Quote', desc: 'You receive a full written quote with itemized costs — no vague estimates, no surprises later.' },
                { step: '03', title: 'We Get to Work', desc: 'Our crew starts on schedule, keeps the job site clean, and updates you at every milestone.' },
                { step: '04', title: 'Final Walkthrough', desc: 'We do a full walkthrough with you before calling the job complete. Your satisfaction is the finish line.' },
              ].map((step, i, arr) => (
                <div key={step.step} className="flex flex-col items-center text-center relative">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#2B5741] rounded-full mb-5 flex-shrink-0 relative z-10">
                    <span
                      className="text-[#C8A97E] text-sm font-bold"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {step.step}
                    </span>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="hidden md:block absolute top-7 left-1/2 w-full h-px bg-[#2B5741]/20" style={{ left: '57%', width: '86%' }}></div>
                  )}
                  <h3
                    className="text-gray-800 font-bold text-sm mb-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Gallery ── */}
        <section className="bg-[#F7F5F1] py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">PAST WORK</p>
              <h2
                className="text-[#2B5741] text-4xl font-bold mb-3"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Recent Renovation Projects
              </h2>
              <p className="text-gray-500 text-sm">
                A sample of completed renovations across the Burlington area.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {galleryItems.map((item) => (
                <div key={item.id} className="group overflow-hidden rounded-lg bg-white">
                  <div className="w-full h-56 overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="px-5 py-3.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#C8A97E]">{item.type}</span>
                    <p className="text-gray-800 font-semibold text-sm mt-0.5">{item.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{item.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA Banner ── */}
        <section className="bg-[#2B5741] py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">READY TO START?</p>
            <h2
              className="text-white text-4xl font-bold mb-5"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Let's Renovate Your Home
            </h2>
            <p className="text-white/70 text-sm mb-8 max-w-lg mx-auto">
              Free on-site consultations, detailed quotes, and a crew that shows up on time. Serving Burlington,
              Oakville, Hamilton, and surrounding areas.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/#contact"
                className="bg-[#C8A97E] text-white px-10 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#b8976a] transition-colors whitespace-nowrap rounded-md"
              >
                GET A FREE QUOTE
              </a>
              <a
                href="tel:9058694676"
                className="flex items-center gap-2 border-2 border-white text-white px-10 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-white hover:text-[#2B5741] transition-colors whitespace-nowrap rounded-md"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill"></i>
                </div>
                905-869-4676
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}